import React, { useState, useCallback, useMemo } from "react";
import { Radio, Select, RadioChangeEvent } from "antd";
import { map, filter } from "underscore";
import { getActiveModule, titleCase } from "@ts/utils";
import {
    CrossChannelRatingAndReviewTableWidget,
    TrendsComparisionWidget,
} from "@components/widgets";
import WidgetDrawer from "./WidgetDrawer";
import { showToast } from "@ts/modals";
import {
    IPricingAndDealCompetitionAnalysisWidget,
    ICompareWith,
    PricingCharts,
    MyProductTrendsModules,
    MyProductTrendsTypes,
    AggregationField,
    SubModule,
    ITrendsComparisionWidgetData,
    IAxisWithOpposite,
} from "@ts/common-types";
import {
    comparisonSelectOptions,
    emptyChartData,
    KPIOptions,
    PRICING_CHART_FILTERS_INTIAL_STATE,
    PRICING_DEAL_TRENDS_AXIS_TITLE_MAP,
    pricingModuleRadioOptions,
} from "@ts/constants";
import { BsFillArrowUpRightCircleFill } from "react-icons/bs";
import { TooltipFormatterContextObject } from "highcharts";

const CompareWith: React.FC<
    ICompareWith & {
        oppositeYAxis: AggregationField;
        setOppositeYAxis: (v: AggregationField) => void;
    }
> = ({
    widgetData,
    selectedCompareOption,
    setSelectedCompareOption,
    chartFilters,
    setChartFilters,
    isComparisonRequired,
    oppositeYAxis,
    setOppositeYAxis,
    isKpiSelectionRequired,
    selectedKpi,
    setSelectedKpi,
    activeModule,
    isCrossRegionRequired,
    kpiOptions,
    compareWithOptions,
}) => {
    const skusOptions = useMemo(() => {
        if (selectedCompareOption === MyProductTrendsModules.COMPETITION) {
            if (activeModule === SubModule.CATEGORY_INSIGHTS) {
                return map(widgetData?.competition?.brands || [], (data) => ({
                    value: data.brand,
                    label: titleCase(data.brand),
                }));
            } else {
                return map(widgetData?.competition?.skus || [], (data) => ({
                    value: data.sku,
                    label: data.sku_title
                        ? `${data.sku_title.substring(0, 27)}-[${data.sku}]`
                        : `${data.sku}`,
                }));
            }
        }

        if (selectedCompareOption === MyProductTrendsModules.COMPARISON) {
            if (activeModule === SubModule.CATEGORY_INSIGHTS) {
                return compareWithOptions;
            } else {
                return filter(
                    compareWithOptions,
                    (item) => item.value !== AggregationField.MS_PERCENT
                );
            }
        }
        return compareWithOptions;
    }, [widgetData, selectedCompareOption]);

    const [compareOptions] = useState(
        [
            {
                label: "Cross Channel",
                value: MyProductTrendsModules.CROSS_CHANNEL,
            },
            isCrossRegionRequired
                ? {
                      label: "Cross Region",
                      value: MyProductTrendsModules.CROSS_REGION,
                  }
                : null,
            { label: "Competition", value: MyProductTrendsModules.COMPETITION },
            isComparisonRequired
                ? {
                      label: "Comparison",
                      value: MyProductTrendsModules.COMPARISON,
                  }
                : null,
        ].filter(Boolean)
    );

    const onCompareFilterChangehandler = (e: RadioChangeEvent) => {
        setSelectedCompareOption(e.target.value as MyProductTrendsModules);
    };

    const onChangeSkusHandler = (payload: string[]) => {
        if (payload.length > 5) {
            showToast("danger", "You can select maximum 5 products");
            return;
        }
        setChartFilters({
            type: PricingCharts.PRICE_DEALS_COMPETITION,
            payload,
        });
    };

    const onOppositeYAxisChangeHandler = (value: AggregationField) => {
        setOppositeYAxis(value);
    };

    const onKPIChangeHandler = (value: string) => {
        setSelectedKpi(value);
        setChartFilters({ type: PricingCharts.KPI_SLECTION, payload: value });
    };

    return (
        <div className="w-100 d-flex  gap-4">
            {isKpiSelectionRequired && (
                <div className="d-flex flex-column">
                    <label
                        htmlFor="kpi-selection"
                        className="col-form-label"
                        style={{ fontSize: 16, color: "#6c757d" }}
                    >
                        Select KPI
                    </label>
                    <Select
                        id="kpi-selection"
                        options={kpiOptions}
                        value={selectedKpi}
                        placeholder="Select Kpi"
                        onChange={onKPIChangeHandler}
                        style={{ width: "15ch" }}
                    />
                </div>
            )}
            <div className="d-flex flex-column">
                <label
                    htmlFor="mode-selection"
                    className="col-form-label"
                    style={{ fontSize: 16, color: "#6c757d" }}
                >
                    Select Mode
                </label>
                <Radio.Group
                    className="competition-radio"
                    value={selectedCompareOption}
                    onChange={onCompareFilterChangehandler}
                    buttonStyle="solid"
                >
                    {map(compareOptions || [], (option) => (
                        <Radio.Button key={option.value} value={option.value}>
                            {option.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
            </div>

            {selectedCompareOption === MyProductTrendsModules.CROSS_CHANNEL && (
                <div className="d-flex flex-column">
                    {widgetData?.[MyProductTrendsModules.CROSS_CHANNEL]
                        ?.channels?.length ? (
                        <label
                            htmlFor="mode-selection"
                            className="col-form-label"
                            style={{ fontSize: 16, color: "#6c757d" }}
                        >
                            Select Channels
                        </label>
                    ) : null}
                    <Radio.Group
                        className={
                            widgetData?.[MyProductTrendsModules.CROSS_CHANNEL]
                                ?.channels?.length
                                ? "d-flex flex-wrap gap-2"
                                : "d-none"
                        }
                        onChange={(e) =>
                            setChartFilters({
                                type: PricingCharts.PRICE_DEALS_CHANNEL,
                                payload: e.target.value,
                            })
                        }
                    >
                        {map(
                            widgetData?.[MyProductTrendsModules.CROSS_CHANNEL]
                                ?.channels || [],
                            (channel) => (
                                <Radio
                                    value={channel}
                                    className="badge-radio"
                                    id={`${channel}-badge`}
                                    key={channel}
                                >
                                    <span
                                        className={`badge badge-pill rounded-pill text-bg-${
                                            chartFilters[
                                                PricingCharts
                                                    .PRICE_DEALS_CHANNEL
                                            ] === channel ||
                                            widgetData[
                                                MyProductTrendsModules
                                                    .CROSS_CHANNEL
                                            ].channels?.length === 1
                                                ? "primary"
                                                : "secondary"
                                        }`}
                                        style={{ fontSize: 12 }}
                                    >
                                        {titleCase(channel)}
                                    </span>
                                </Radio>
                            )
                        )}
                    </Radio.Group>
                </div>
            )}

            {selectedCompareOption === MyProductTrendsModules.CROSS_REGION && (
                <div className="d-flex flex-column">
                    <label
                        htmlFor="mode-selection"
                        className="col-form-label"
                        style={{ fontSize: 16, color: "#6c757d" }}
                    >
                        Select Regions
                    </label>
                    <Radio.Group
                        className={
                            widgetData?.[MyProductTrendsModules.CROSS_REGION]
                                ?.channels?.length
                                ? "d-flex flex-wrap gap-2"
                                : "d-none"
                        }
                        onChange={(e) =>
                            setChartFilters({
                                type: PricingCharts.PRICE_DEALS_CHANNEL,
                                payload: e.target.value,
                            })
                        }
                    >
                        {map(
                            widgetData?.[MyProductTrendsModules.CROSS_REGION]
                                ?.regions || [],
                            (region) => (
                                <Radio
                                    value={region}
                                    className="badge-radio"
                                    id={`${region}-badge`}
                                    key={region}
                                >
                                    <span
                                        className={`badge badge-pill rounded-pill text-bg-${
                                            chartFilters[
                                                PricingCharts
                                                    .PRICE_DEALS_CHANNEL
                                            ] === region ||
                                            widgetData[
                                                MyProductTrendsModules
                                                    .CROSS_REGION
                                            ].regions?.length === 1
                                                ? "primary"
                                                : "secondary"
                                        }`}
                                        style={{ fontSize: 12 }}
                                    >
                                        {titleCase(region)}
                                    </span>
                                </Radio>
                            )
                        )}
                    </Radio.Group>
                </div>
            )}

            {selectedCompareOption === MyProductTrendsModules.COMPARISON && (
                <div className="d-flex flex-column">
                    <label
                        htmlFor="comparison"
                        className="col-form-label"
                        style={{ fontSize: 16, color: "#6c757d" }}
                    >
                        {activeModule === SubModule.CATEGORY_INSIGHTS
                            ? "Compare With"
                            : "Select Metric:"}
                    </label>
                    <Select
                        id="comparison"
                        options={skusOptions}
                        value={oppositeYAxis}
                        placeholder="Select Metric"
                        onChange={onOppositeYAxisChangeHandler}
                        style={{ width: "25ch" }}
                    />
                </div>
            )}

            {selectedCompareOption === MyProductTrendsModules.COMPETITION && (
                <div className="d-flex flex-column">
                    <label
                        htmlFor="competition"
                        className="col-form-label"
                        style={{ fontSize: 16 }}
                    >
                        {activeModule === SubModule.CATEGORY_INSIGHTS
                            ? "Select Brand"
                            : "Select Product"}
                    </label>
                    <Select
                        id="competition"
                        options={skusOptions}
                        mode="multiple"
                        showSearch
                        maxTagCount={1}
                        value={
                            chartFilters[PricingCharts.PRICE_DEALS_COMPETITION]
                        }
                        placeholder={
                            activeModule === SubModule.CATEGORY_INSIGHTS
                                ? "Select Brand"
                                : "Select Product"
                        }
                        onChange={onChangeSkusHandler}
                        maxTagTextLength={10}
                        allowClear
                        style={{ width: "27ch" }}
                    />
                </div>
            )}
        </div>
    );
};

const PricingAndDealCompetitionAnalysisWidget: React.FC<
    IPricingAndDealCompetitionAnalysisWidget
> = ({
    title,
    show,
    setShow,
    widgetData,
    chartFilters,
    setChartFilters,
    currencySymbol,
    hideDrawerChartSwitchOptions = false,
    radioOptions = pricingModuleRadioOptions,
    isComparisonRequired = false,
    isSubCategoryDropDownOptionsRequired = false,
    subCategories,
    isKpiSelectionRequired = false,
    selectedTrendsSubCategory,
    setSelectedTrendsSubCategory,
    isGoToDrainersRequired = false,
    setShowDrainers,
    isCrossRegionRequired = false,
    kpiOptions = KPIOptions,
    showMoreButtonRequired = true,
    compareWithOptions = comparisonSelectOptions,
}) => {
    const { activeModule } = getActiveModule();

    const [selectedCompareOption, setSelectedCompareOption] = useState(
        MyProductTrendsModules.CROSS_CHANNEL
    );

    const [selectedKpi, setSelectedKpi] = useState<string>(
        AggregationField.MS_PERCENT
    );

    const oppositeYAxisDefault =
        activeModule === SubModule.CATEGORY_INSIGHTS
            ? AggregationField.MS_PERCENT
            : AggregationField.ROAS;

    const [oppositeYAxis, setOppositeYAxis] = useState(oppositeYAxisDefault);

    const onClose = () => {
        setShow(false);
    };

    const drawerTitle = useCallback(
        (title: string) => (
            <div className="d-flex align-items-center justify-content-between w-100">
                <div className="fs-5 fw-medium w-50">{title}</div>
                {!hideDrawerChartSwitchOptions && (
                    <div className="d-flex align-items-center justify-content-end fs-6 pe-4">
                        <Radio.Group
                            options={
                                radioOptions as {
                                    label: string;
                                    value: string | number;
                                }[]
                            }
                            onChange={(e) =>
                                setChartFilters({
                                    type: PricingCharts.PRICE_DEALS,
                                    payload: e.target.value,
                                })
                            }
                            value={chartFilters[PricingCharts.PRICE_DEALS]}
                            optionType="button"
                            buttonStyle="solid"
                        />
                    </div>
                )}
                {isSubCategoryDropDownOptionsRequired && (
                    <div
                        className="d-flex align-items-center justify-content-end"
                        style={{ width: "60vw" }}
                    >
                        <div className="d-flex align-items-center justify-content-end fs-6 gap-2 w-50 me-4">
                            <label style={{ fontSize: 16 }}>
                                Sub-Category:
                            </label>
                            <Select
                                className=""
                                style={{ width: "11vw" }}
                                showSearch
                                value={selectedTrendsSubCategory}
                                options={[
                                    { label: "All", value: "all" },
                                    ...subCategories.map((subCat) => ({
                                        label: subCat,
                                        value: subCat,
                                    })),
                                ]}
                                onChange={(value) => {
                                    setSelectedTrendsSubCategory(
                                        value as string
                                    );
                                }}
                                filterOption={(input, option) =>
                                    (option?.label as string)
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </div>

                        {isGoToDrainersRequired && (
                            <div
                                className="text-primary fs-6 mx-4"
                                role="button"
                                onClick={() => {
                                    setShowDrainers(true);
                                    setShow(false);
                                }}
                            >
                                Drainers <BsFillArrowUpRightCircleFill />
                            </div>
                        )}
                    </div>
                )}
            </div>
        ),
        [
            widgetData,
            chartFilters,
            setChartFilters,
            radioOptions,
            hideDrawerChartSwitchOptions,
        ]
    );

    const getWidgetData = (): ITrendsComparisionWidgetData => {
        const chartKey = isKpiSelectionRequired
            ? selectedKpi
            : chartFilters[PricingCharts.PRICE_DEALS];
        return (
            widgetData[selectedCompareOption]?.charts[chartKey] ||
            emptyChartData
        );
    };

    const chartPointsTooltipFormatter = (
        widgetData: any,
        selectedCompareOption: string,
        chartFilters: typeof PRICING_CHART_FILTERS_INTIAL_STATE,
        selectedKpi: string,
        isKpiSelectionRequired: boolean,
        currencySymbol: string,
        oppositeYAxis: string
    ) => {
        return function (this: TooltipFormatterContextObject): string {
            const chartKey = isKpiSelectionRequired
                ? selectedKpi
                : chartFilters[PricingCharts.PRICE_DEALS];

            const chartData =
                widgetData[selectedCompareOption]?.charts?.[chartKey];

            const skuTitle = chartData?.skuTitles?.[this.series.name] || "N/A";
            const filterType = chartFilters[PricingCharts.PRICE_DEALS];

            const isDeals = filterType === MyProductTrendsTypes.DEALS;
            const isPrice = filterType === MyProductTrendsTypes.PRICE;

            const units = isDeals ? "%" : isPrice ? currencySymbol : null;

            let productNameHtml = "";
            if (selectedCompareOption === MyProductTrendsModules.COMPETITION) {
                productNameHtml =
                    activeModule === SubModule.CATEGORY_INSIGHTS
                        ? `<b>Brand Name:</b> ${skuTitle} <br><br>`
                        : `<b>Product Name:</b> ${skuTitle} <br><br>`;
            }

            const value = this.y;
            const valueHtml = units
                ? isDeals
                    ? `${value} ${units}`
                    : `${units} ${value}`
                : value;

            const valueHtmlKey = titleCase(chartKey);

            const isOppositeAxis =
                (this.series?.yAxis as IAxisWithOpposite)?.opposite === true;
            const axisLabel = isOppositeAxis
                ? `<b>${titleCase(oppositeYAxis)}:</b> ${valueHtml}`
                : `<b>${valueHtmlKey}:</b> ${valueHtml}`;

            return `${productNameHtml}${axisLabel}`;
        };
    };

    const TrendComparisonWidgetYaxisTitle = useMemo(() => {
        return PRICING_DEAL_TRENDS_AXIS_TITLE_MAP[
            chartFilters[PricingCharts.PRICE_DEALS] ||
                chartFilters[PricingCharts.KPI_SLECTION]
        ]
            ? PRICING_DEAL_TRENDS_AXIS_TITLE_MAP[
                  chartFilters[PricingCharts.PRICE_DEALS] ||
                      chartFilters[PricingCharts.KPI_SLECTION]
              ]
            : titleCase(selectedKpi);
    }, [chartFilters, selectedKpi]);

    return (
        <WidgetDrawer
            title={drawerTitle(title)}
            width="85vw"
            show={show}
            onClose={onClose}
            bodyStyle={{ backgroundColor: "#FFFFFF" }}
        >
            <CompareWith
                widgetData={widgetData}
                selectedCompareOption={selectedCompareOption}
                setSelectedCompareOption={setSelectedCompareOption}
                chartFilters={chartFilters}
                setChartFilters={setChartFilters}
                isComparisonRequired={isComparisonRequired}
                oppositeYAxis={oppositeYAxis}
                setOppositeYAxis={setOppositeYAxis}
                isKpiSelectionRequired={isKpiSelectionRequired}
                selectedKpi={selectedKpi}
                setSelectedKpi={setSelectedKpi}
                activeModule={activeModule}
                isCrossRegionRequired={isCrossRegionRequired}
                kpiOptions={kpiOptions}
                compareWithOptions={compareWithOptions}
            />
            <TrendsComparisionWidget
                title=""
                isTitleRequired={false}
                isCardRequired={false}
                widgetData={getWidgetData()}
                graphAttributes={{
                    yAxisTitle: TrendComparisonWidgetYaxisTitle,
                    oppositeYAxisTitle:
                        selectedCompareOption ===
                        MyProductTrendsModules.COMPARISON
                            ? titleCase(oppositeYAxis)
                            : undefined,
                    formatter: chartPointsTooltipFormatter(
                        widgetData,
                        selectedCompareOption,
                        chartFilters,
                        selectedKpi,
                        isKpiSelectionRequired,
                        currencySymbol,
                        oppositeYAxis
                    ),
                }}
                selectedCompareOption={selectedCompareOption}
                yAxis={selectedKpi}
                oppositeYAxis={oppositeYAxis}
                compareWithOptions={compareWithOptions}
            />
            <CrossChannelRatingAndReviewTableWidget
                widgetData={widgetData?.[selectedCompareOption]?.table}
                expandLabel="Show Competition Price"
                showMoreButtonRequired={showMoreButtonRequired}
            />
        </WidgetDrawer>
    );
};

export default PricingAndDealCompetitionAnalysisWidget;
